import type { CreatorProfile } from '../../onboarding/types';
import { normalizeEnum, normalizeText } from '../../shared/normalize';
import {
	CREATOR_PLATFORMS,
	type CreatorPlatform,
	PLATFORM_LABELS,
} from '../../shared/platforms';
import { HISTORY_TEXT } from '../content';
import { PROGRAMME_TERMS } from '../programme';
import { HAND_VERIFIED } from '../social/platform-meta';
import {
	type AnalyticsBreakdown,
	COMMISSION_STATUSES,
	type Commission,
	type CommissionsPage,
	type ConnectedPlatform,
	type CreatorAnalytics,
	type CreatorEarnings,
	type CreatorSummary,
	type Lob,
	PAYOUT_BLOCKED_REASONS,
	type PastPayout,
	type PayoutDestination,
	type PendingChannel,
	WITHDRAWAL_STATUSES,
	type WithdrawalRequest,
	type WithdrawalResult,
} from '../types';
import type {
	AnalyticsDto,
	AnalyticsTotalsDto,
	BookingItemDto,
	BookingsDto,
	EarningsDto,
	PayoutDto,
	PayoutRequestDto,
	ProfileDto,
} from './creators-api.types';

const PRODUCT_TYPES = ['FLIGHT', 'HOTEL'] as const;

const LOB_OF: Record<(typeof PRODUCT_TYPES)[number], Lob> = {
	FLIGHT: 'flights',
	HOTEL: 'hotels',
};

/** The channel's name, else its @handle, else its link, as on the onboarding
 *  status screens. */
function channelName(
	{
		displayName,
		handle,
		link,
	}: { displayName?: string; handle?: string; link?: string },
	platform: CreatorPlatform,
): string {
	const atHandle = normalizeText(handle);
	return (
		normalizeText(displayName) ||
		(atHandle ? `@${atHandle}` : normalizeText(link)) ||
		PLATFORM_LABELS[platform]
	);
}

function mapPendingChannel(dto: ProfileDto): PendingChannel | null {
	const platform = normalizeEnum(dto.platform, CREATOR_PLATFORMS);
	if (!platform) return null;
	return {
		platform,
		name: channelName(
			{
				displayName: dto.display_name,
				handle: dto.handle,
				link: dto.profile_url,
			},
			platform,
		),
		audience: dto.follower_count ?? 0,
	};
}

const DESTINATION_CODES = ['IM', 'IMM'] as const;

const DESTINATION_OF: Record<
	(typeof DESTINATION_CODES)[number],
	PayoutDestination
> = {
	IM: 'ixigo_money',
	IMM: 'ixigo_money_max',
};

function mapPastPayout(dto: PayoutDto, index: number): PastPayout | null {
	const code = normalizeEnum(dto.destination, DESTINATION_CODES);
	const createdAt = normalizeText(dto.created_at);
	if (!code || !createdAt) return null;
	return {
		id: normalizeText(dto.transaction_id) || `payout-${index}`,
		destination: DESTINATION_OF[code],
		// As for a withdrawal just made: unrecognised means not settled yet.
		status: normalizeEnum(dto.status, WITHDRAWAL_STATUSES) ?? 'processing',
		withdrawn: dto.commission_amount ?? 0,
		bonus: dto.bonus_amount ?? 0,
		credited: dto.credited_amount ?? 0,
		createdAt,
	};
}

export function mapEarningsDto(dto: EarningsDto): CreatorSummary {
	const withdrawal = dto.withdrawal ?? {};
	const earnings: CreatorEarnings = {
		pending: dto.earnings?.pending ?? 0,
		confirmed: dto.earnings?.confirmed ?? 0,
		paid: dto.earnings?.paid ?? 0,
		bookings: dto.bookings ?? 0,
	};
	const minPayout = withdrawal.minimum ?? 0;
	return {
		profile: {
			code: normalizeText(dto.coupon_code),
			liveSince: dto.live_since ?? null,
		},
		home: { ...PROGRAMME_TERMS, earnings, minPayout },
		payout: {
			earnings,
			minPayout,
			ixigoMoneyBonusPercent: withdrawal.im_bonus_percent ?? 0,
			// Not sent by the API yet, so the terms link stays hidden.
			bonusTermsUrl: null,
			payout: {
				canWithdraw: withdrawal.can_withdraw ?? false,
				blockedReason: normalizeEnum(
					withdrawal.blocked_reason,
					PAYOUT_BLOCKED_REASONS,
				),
				balance: withdrawal.balance ?? 0,
				pendingChannels: (withdrawal.channels_awaiting_verification ?? [])
					.map(mapPendingChannel)
					.filter((channel) => channel !== null),
				quote: {
					ixigoMoney: withdrawal.im_amount ?? 0,
					ixigoMoneyMax: withdrawal.imm_amount ?? 0,
				},
			},
			// Payouts it can't place (an unknown destination, no date) are left
			// out.
			previousPayouts: (dto.recent_payouts ?? [])
				.map(mapPastPayout)
				.filter((payout) => payout !== null),
		},
	};
}

function mapBookingItemDto(
	dto: BookingItemDto,
	standInId: string,
): Commission | null {
	const product = normalizeEnum(dto.product_type, PRODUCT_TYPES);
	if (!product) return null;
	const type = LOB_OF[product];
	const start = normalizeText(dto.travel_start_date) || null;
	const end = normalizeText(dto.travel_end_date) || null;
	return {
		// The booking id can be missing while the booking is still syncing.
		id: normalizeText(dto.booking_id) || standInId,
		type,
		title: normalizeText(dto.title) || HISTORY_TEXT.syncingTitle[type],
		travelStart: start,
		// A one-way flight comes with the same start and end date.
		travelEnd: end && end !== start ? end : null,
		status: normalizeEnum(dto.status, COMMISSION_STATUSES) ?? 'pending',
		commission: dto.commission_amount ?? 0,
	};
}

export function mapBookingsDto(dto: BookingsDto): CommissionsPage {
	const page = dto.page ?? 0;
	const next = page + 1;
	return {
		items: (dto.items ?? [])
			.map((item, index) => mapBookingItemDto(item, `syncing-${page}-${index}`))
			.filter((item) => item !== null),
		// The page to load next, while there is one.
		nextCursor: next < (dto.total_pages ?? 0) ? String(next) : null,
	};
}

const DESTINATION_CODE: Record<
	PayoutDestination,
	PayoutRequestDto['destination']
> = {
	ixigo_money: 'IM',
	ixigo_money_max: 'IMM',
};

export function toPayoutRequestDto(
	request: WithdrawalRequest,
): PayoutRequestDto {
	return {
		destination: DESTINATION_CODE[request.destination],
		amount: request.amount,
		request_id: request.requestId,
	};
}

export function mapPayoutDto(dto: PayoutDto): WithdrawalResult {
	return {
		// An unrecognised status is treated as still processing, so the
		// balance is reloaded rather than reported as paid or failed.
		status: normalizeEnum(dto.status, WITHDRAWAL_STATUSES) ?? 'processing',
		creditedAmount: dto.credited_amount ?? 0,
		transactionId: normalizeText(dto.transaction_id),
	};
}

function mapAnalyticsTotalsDto(
	dto: AnalyticsTotalsDto = {},
): AnalyticsBreakdown {
	const bookings = dto.bookings ?? 0;
	return {
		earnings: dto.earnings ?? 0,
		codeApplied: dto.applied ?? 0,
		bookings,
		averageOrderValue: dto.average_order_value ?? 0,
		// Every booking used the code once.
		couponsUsed: bookings,
		weeks: (dto.weekly ?? []).flatMap((week) =>
			week.week_start
				? [
						{
							start: week.week_start,
							earnings: week.earnings ?? 0,
							bookings: week.bookings ?? 0,
						},
					]
				: [],
		),
	};
}

export function mapAnalyticsDto(dto: AnalyticsDto): CreatorAnalytics {
	return {
		period: { from: normalizeText(dto.from), to: normalizeText(dto.to) },
		all: mapAnalyticsTotalsDto(dto.overall),
		flights: mapAnalyticsTotalsDto(dto.flight),
		hotels: mapAnalyticsTotalsDto(dto.hotel),
	};
}

/** A profile from the creator's details, as a Social card. A pending
 *  WhatsApp / Telegram channel is live and waiting on ops; any other pending
 *  platform is still in review (the backend's own rule). */
export function mapConnectedPlatform(
	profile: CreatorProfile,
): ConnectedPlatform {
	const status =
		profile.verificationStatus === 'VERIFIED'
			? 'verified'
			: profile.verificationStatus === 'REJECTED'
				? 'rejected'
				: HAND_VERIFIED.has(profile.platform)
					? 'verification_pending'
					: 'in_review';
	return {
		id: profile.id,
		platform: profile.platform,
		name: channelName(
			{
				displayName: profile.displayName,
				handle: profile.handle,
				link: profile.profileUrl,
			},
			profile.platform,
		),
		status,
		addedAt: profile.addedAt,
		verifiedAt: profile.verifiedAt,
	};
}
